import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { Effects } from '@/config/effects';
import { PrintArea } from '@/types';
import { sortInstancesByLayerOrder } from '@utils/layerUtils';

interface PrintAreaTextureProps {
    printArea: PrintArea;
}

declare global {
    interface Window {
        _getPrintAreaCanvas?: (printAreaId: string) => HTMLCanvasElement | null;
        _registerPrintAreaCanvas?: (id: string, getCanvas: () => HTMLCanvasElement | null) => void;
        _printAreaCanvases: Map<string, () => HTMLCanvasElement | null>;
    }
}

// Initialize registry if needed
if (!window._printAreaCanvases) {
    window._printAreaCanvases = new Map();
    window._getPrintAreaCanvas = (id: string) => {
        const getter = window._printAreaCanvases.get(id);
        return getter ? getter() : null;
    };
}

export default function PrintAreaTexture({ printArea }: PrintAreaTextureProps) {
    const {
        logoInstances,
        textInstances,
        logos,
        currentModel,
    } = useConfiguratorStore();

    // Track font loading — Canvas API requires fonts to be fully loaded before rendering.
    // Without this, text may render with a fallback system font on first paint.
    const [fontsReady, setFontsReady] = useState(false);
    useEffect(() => {
        document.fonts.ready.then(() => setFontsReady(true));
    }, []);

    // Force re-render when instances list ref changes (Redo creates new array)
    // The main useEffect already depends on logoInstances, so this is just ensuring consistency

    // Refs for material properties
    const originalMapRef = useRef<THREE.Texture | null>(null);
    const originalNormalMapRef = useRef<THREE.Texture | null>(null);
    const originalRoughnessMapRef = useRef<THREE.Texture | null>(null);
    const originalMapImageRef = useRef<HTMLImageElement | null>(null);

    // Canvas refs - initialized in useEffect
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const textureRef = useRef<THREE.CanvasTexture | null>(null);
    const bumpCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const bumpTextureRef = useRef<THREE.CanvasTexture | null>(null);
    const roughnessCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const roughnessTextureRef = useRef<THREE.CanvasTexture | null>(null);
    const metalnessCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const metalnessTextureRef = useRef<THREE.CanvasTexture | null>(null);
    const normalCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const normalTextureRef = useRef<THREE.CanvasTexture | null>(null);

    // Effect texture cache
    const effectTextureCache = useRef<Map<string, HTMLImageElement>>(new Map());

    // Logo image cache - prevents async loading during drag
    const logoImageCache = useRef<Map<string, HTMLImageElement>>(new Map());

    // Helper to load image - uses cache for instant loading during drag
    const loadImage = (src: string): Promise<HTMLImageElement> => {
        // Check cache first for instant return
        const cached = logoImageCache.current.get(src);
        if (cached) {
            // Ensure complete
            if (cached.complete) return Promise.resolve(cached);
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                // Cache the loaded image
                logoImageCache.current.set(src, img);
                resolve(img);
            };
            img.onerror = () => {
                console.error(`Failed to load image: ${src}`);
                reject(new Error(`Failed to load image: ${src}`));
            };
            img.src = src;
        });
    };

    // Preload effect textures
    useEffect(() => {
        Object.values(Effects).forEach(effect => {
            if (effect.textures) {
                Object.values(effect.textures).forEach(url => {
                    if (url && !effectTextureCache.current.has(url)) {
                        loadImage(url).then(img => {
                            effectTextureCache.current.set(url, img);
                        }).catch(err => console.error('Failed to load effect texture:', url, err));
                    }
                });
            }
        });
    }, []);

    // Capture initial material textures (including borrowed ones)
    useEffect(() => {
        const captureTextures = async () => {
            if (printArea.meshRef?.material) {
                const mat = printArea.meshRef.material as THREE.MeshStandardMaterial;

                // Capture Normal & Roughness
                if (mat.normalMap && !originalNormalMapRef.current) originalNormalMapRef.current = mat.normalMap;
                if (mat.roughnessMap && !originalRoughnessMapRef.current) originalRoughnessMapRef.current = mat.roughnessMap;

                // Capture Diffuse (Map)
                // Priority: 1. Material's own map, 2. Borrowed texture from sibling
                let diffuseMap = mat.map;

                if (!diffuseMap && printArea.borrowedDiffuseTexture) {
                    diffuseMap = printArea.borrowedDiffuseTexture;
                }

                if (diffuseMap && !originalMapImageRef.current) {
                    originalMapRef.current = diffuseMap;

                    // Extract image data for canvas compositing
                    const image = diffuseMap.source.data;

                    if (image instanceof HTMLImageElement) {
                        if (!image.complete) {
                            await new Promise((resolve) => {
                                image.onload = resolve;
                                image.onerror = resolve;
                            });
                        }
                        originalMapImageRef.current = image;
                    } else if (image instanceof ImageBitmap) {
                        // Convert ImageBitmap to HTMLImageElement via canvas
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = image.width;
                        tempCanvas.height = image.height;
                        const tempCtx = tempCanvas.getContext('2d');
                        if (tempCtx) {
                            tempCtx.drawImage(image, 0, 0);
                            const newImg = new Image();
                            newImg.src = tempCanvas.toDataURL();
                            originalMapImageRef.current = newImg;
                        }
                    } else if (image && (image instanceof HTMLCanvasElement || (typeof OffscreenCanvas !== 'undefined' && image instanceof OffscreenCanvas))) {
                        // Handle Canvas textures
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = image.width;
                        tempCanvas.height = image.height;
                        const tempCtx = tempCanvas.getContext('2d');
                        if (tempCtx) {
                            tempCtx.drawImage(image as CanvasImageSource, 0, 0);
                            const newImg = new Image();
                            newImg.src = tempCanvas.toDataURL();
                            originalMapImageRef.current = newImg;
                        }
                    }
                }
            }
        };

        captureTextures();
    }, [printArea.meshRef, printArea.borrowedDiffuseTexture]);

    // Register this canvas getter
    useEffect(() => {
        if (!window._printAreaCanvases) window._printAreaCanvases = new Map();
        
        window._printAreaCanvases.set(printArea.id, () => canvasRef.current);

        return () => {
             // Cleanup
             if (window._printAreaCanvases) {
                 window._printAreaCanvases.delete(printArea.id);
             }
        };
    }, [printArea.id]);

    // Cleanup textures on unmount to prevent VRAM leaks
    useEffect(() => {
        return () => {
            if (textureRef.current) textureRef.current.dispose();
            if (bumpTextureRef.current) bumpTextureRef.current.dispose();
            if (roughnessTextureRef.current) roughnessTextureRef.current.dispose();
            if (metalnessTextureRef.current) metalnessTextureRef.current.dispose();
            if (normalTextureRef.current) normalTextureRef.current.dispose();
        };
    }, []);

    // Main Rendering Logic
    useEffect(() => {
        if (!printArea.meshRef) return;

        // Initialize Canvases
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
            canvasRef.current.width = 1024;
            canvasRef.current.height = 1024;
        }
        if (!bumpCanvasRef.current) {
            bumpCanvasRef.current = document.createElement('canvas');
            bumpCanvasRef.current.width = 1024;
            bumpCanvasRef.current.height = 1024;
        }
        if (!roughnessCanvasRef.current) {
            roughnessCanvasRef.current = document.createElement('canvas');
            roughnessCanvasRef.current.width = 1024;
            roughnessCanvasRef.current.height = 1024;
        }
        if (!metalnessCanvasRef.current) {
            metalnessCanvasRef.current = document.createElement('canvas');
            metalnessCanvasRef.current.width = 1024;
            metalnessCanvasRef.current.height = 1024;
        }
        if (!normalCanvasRef.current) {
            normalCanvasRef.current = document.createElement('canvas');
            normalCanvasRef.current.width = 1024;
            normalCanvasRef.current.height = 1024;
        }

        const colorCtx = canvasRef.current.getContext('2d');
        const bumpCtx = bumpCanvasRef.current.getContext('2d');
        const roughCtx = roughnessCanvasRef.current.getContext('2d');
        const metalnessCtx = metalnessCanvasRef.current.getContext('2d');
        const normalCtx = normalCanvasRef.current.getContext('2d');

        if (!colorCtx || !bumpCtx || !roughCtx || !metalnessCtx || !normalCtx) return;

        // Clear all canvases
        colorCtx.clearRect(0, 0, 1024, 1024);
        bumpCtx.clearRect(0, 0, 1024, 1024);
        metalnessCtx.fillStyle = '#000000'; // Default non-metal
        metalnessCtx.fillRect(0, 0, 1024, 1024);
        roughCtx.fillStyle = '#FFFFFF'; // Default roughness (matte)
        roughCtx.fillRect(0, 0, 1024, 1024);

        // Fill with Background Color if set
        if (printArea.backgroundColor) {
            colorCtx.fillStyle = printArea.backgroundColor;
            colorCtx.fillRect(0, 0, 1024, 1024);
        }

        // Clear Normal - Use Neutral Normal (128, 128, 255) where no logo exists? 
        // Or transparent? Standard Material treats transparent normal map pixels as... ?
        // If we want transparency, we clearRect.
        normalCtx.clearRect(0, 0, 1024, 1024);

        // Filter instances for this print area
        // For "all-faces": show logos that belong to the group (created from "all-faces" selection)
        // For individual areas: show only logos added directly to that area (not grouped)
        let areaLogos = [];
        let areaTexts = [];
        
        if (printArea.id === 'all-faces') {
            // All Faces canvas view: show only the single instance assigned to 'all-faces'
            areaLogos = logoInstances.filter(l => l.printAreaId === 'all-faces');
            areaTexts = textInstances.filter(t => t.printAreaId === 'all-faces');
        } else {
            // Individual print area: show logos added directly to this area
            // For 3D composition include any 'all-faces' logos so they render across all meshes
            const perAreaLogos = logoInstances.filter(l => l.printAreaId === printArea.id);
            
            // For Square Pen, the 'all-faces' wrapper is visible and handles the wrap-around texture.
            // Don't distribute to individual faces to avoid duplication.
            const allFaceLogos = (currentModel === 'square-pen') 
                ? [] 
                : logoInstances.filter(l => l.printAreaId === 'all-faces');
            
            // Deduplicate: prefer explicit per-area placements over all-faces duplicates
            const logoById = new Map<string, any>();
            perAreaLogos.forEach(l => logoById.set(l.logoId, l));
            allFaceLogos.forEach(l => { if (!logoById.has(l.logoId)) logoById.set(l.logoId, l); });
            areaLogos = Array.from(logoById.values());

            const perAreaTexts = textInstances.filter(t => t.printAreaId === printArea.id);
            const allFaceTexts = (currentModel === 'square-pen')
                ? []
                : textInstances.filter(t => t.printAreaId === 'all-faces');

            const textByKey = new Map<string, any>();
            perAreaTexts.forEach(t => textByKey.set(t.text + '::' + t.id, t));
            allFaceTexts.forEach(t => { if (!Array.from(textByKey.values()).some(existing => existing.text === t.text)) textByKey.set(t.text + '::' + t.id, t); });
            areaTexts = Array.from(textByKey.values());
        }

        // Helper scratch canvas for operations
        const getScratchCanvas = (w: number, h: number) => {
            const cvs = document.createElement('canvas');
            cvs.width = w;
            cvs.height = h;
            return { cvs, ctx: cvs.getContext('2d')! };
        };

        // Render Function
        const render = async () => {
            // Don't draw background - keep canvas transparent
            // Only logos and text will be drawn, rest stays transparent

            // Determine configuration flags
            const useInvertX = printArea.invertX ?? false;
            const useInvertY = printArea.invertY ?? printArea.uvBounds?.needsFlipY ?? false;
            // Default texture flip to true (Standard) if not explicitly set
            const useTextureFlip = printArea.textureFlip !== undefined ? printArea.textureFlip : true;
            
            // SPECIAL FIX: Cap Back_Strip_Print_Area needs horizontal flip
            // Check if this is the cap's back strip print area (mirrored)
            const isCapBackStrip = currentModel === 'cap' && 
                                   (printArea.name.toLowerCase().includes('back') && 
                                    printArea.name.toLowerCase().includes('strip'));
            
            const useTextureFlipX = isCapBackStrip 
                ? !(printArea.textureFlipX ?? false)  // Invert the flip for back strip
                : (printArea.textureFlipX ?? false);  // Use normal for others


            // Shared Effect Rendering Logic (Logos + Text)
            const applyEffect = (
                ctx: CanvasRenderingContext2D,
                source: CanvasImageSource,
                w: number,
                h: number,
                effectDef: any,
                passType: 'color' | 'bump' | 'roughness' | 'metalness' | 'normal'
            ) => {
                // SCREEN PRINT
                if (effectDef.id === 'screen_print') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    if (passType === 'color') {
                        // Color pass: just image relative to position
                    } else if (passType === 'bump') {
                        // White = High point (Ink thickness)
                        sc.fillStyle = "#FFFFFF";
                        sc.fillRect(0, 0, w, h);
                    } else if (passType === 'roughness') {
                        // 0.4 Roughness ~ #666666 (Semi-gloss)
                        const rVal = Math.floor(effectDef.materialProps.roughness * 255);
                        const hex = rVal.toString(16).padStart(2, '0');
                        sc.fillStyle = `#${hex}${hex}${hex}`;
                        sc.fillRect(0, 0, w, h);
                    }

                    if (passType === 'color') {
                        ctx.drawImage(source, -w / 2, -h / 2, w, h);
                    } else {
                        // Draw the mask (bump/roughness)
                        ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    }
                    return;
                }

                // SUBLIMATION
                if (effectDef.id === 'sublimation') {
                    // Skip Bump/Roughness for sublimation
                    if (passType !== 'color') {
                        return;
                    }

                    // Color Pass: Multiply with fabric texture
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // 1. Draw Content
                    sc.drawImage(source, 0, 0, w, h);

                    // 2. Multiply Fabric details
                    if (originalMapImageRef.current) {
                        sc.globalCompositeOperation = "multiply";
                        
                        // SPECIAL: For Lanyards, the borrowed texture is the full UV map (Strap).
                        // We must STRETCH it to fit the 'All Faces' canvas, creating a 1:1 mapping.
                        const isLanyard = currentModel?.includes('lanyard');
                        
                        if (isLanyard) {
                             sc.drawImage(originalMapImageRef.current, 0, 0, w, h);
                        } else {
                             // Default: Tile the texture (e.g. Towel grain)
                             const pattern = sc.createPattern(originalMapImageRef.current, 'repeat');
                             if (pattern) {
                                 sc.fillStyle = pattern;
                                 sc.fillRect(0, 0, w, h);
                             }
                        }

                        // 3. Clip back to content
                        sc.globalCompositeOperation = "destination-in";
                        sc.drawImage(source, 0, 0, w, h);
                    }

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }

                // GLASS ETCHING
                if (effectDef.id === 'glass_etching') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // Draw as mask
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    if (passType === 'color') {
                        const diffuseImg = effectDef.textures.diffuse ? effectTextureCache.current.get(effectDef.textures.diffuse) : null;
                        if (diffuseImg) {
                            sc.drawImage(diffuseImg, 0, 0, w, h);
                        } else {
                            // High opacity to block the underlying "silver" glass material for a matte white look
                            sc.fillStyle = "rgba(255, 255, 255, 0.85)";
                            sc.fillRect(0, 0, w, h);

                            // CLEANUP: Clear 2px border to remove potential "Box Line" artifacts 
                            // caused by dirty alpha at the edge of the uploaded image or texture wrapping
                            sc.clearRect(0, 0, w, 2); // Top
                            sc.clearRect(0, h - 2, w, 2); // Bottom
                            sc.clearRect(0, 0, 2, h); // Left
                            sc.clearRect(w - 2, 0, 2, h); // Right
                        }

                        // Add Inner Shadow (Depth)
                        const { cvs: aoMap, ctx: ao } = getScratchCanvas(w, h);
                        ao.save();
                        // AO Shape Generation
                        ao.translate(w / 2, h / 2);
                        ao.scale(0.97, 0.97);
                        ao.filter = "blur(3px)";
                        ao.drawImage(source, -w / 2, -h / 2, w, h);
                        ao.restore();

                        // White Center
                        ao.globalCompositeOperation = "source-in";
                        ao.fillStyle = "white";
                        ao.fillRect(0, 0, w, h);

                        // Gray Edges
                        ao.globalCompositeOperation = "destination-over";
                        ao.fillStyle = "#909090"; // Subtle gray shadow for glass depth
                        ao.fillRect(0, 0, w, h);

                        // Multiply onto Color
                        sc.globalCompositeOperation = "multiply";
                        sc.drawImage(aoMap, 0, 0, w, h);

                        // Clip Overflow
                        sc.globalCompositeOperation = "destination-in";
                        sc.drawImage(source, 0, 0, w, h);

                    } else if (passType === 'bump') {
                        // Soften the white fill to create sloping walls (Bevel)
                        // White = Low (with negative bumpScale)
                        sc.clearRect(0, 0, w, h);
                        sc.filter = "blur(3px)"; // Soft bevel
                        sc.drawImage(source, 0, 0, w, h);
                        sc.filter = "none";

                        sc.globalCompositeOperation = "source-in";
                        sc.fillStyle = "#FFFFFF";
                        sc.fillRect(0, 0, w, h);

                        const normalImg = effectDef.textures.normal ? effectTextureCache.current.get(effectDef.textures.normal) : null;
                        if (normalImg) {
                            sc.globalCompositeOperation = "multiply";
                            sc.drawImage(normalImg, 0, 0, w, h);
                        }
                    } else if (passType === 'roughness') {
                        const roughImg = effectDef.textures.roughness ? effectTextureCache.current.get(effectDef.textures.roughness) : null;
                        if (roughImg) {
                            sc.drawImage(roughImg, 0, 0, w, h);
                        } else {
                            sc.fillStyle = "#FFFFFF";
                            sc.fillRect(0, 0, w, h);
                        }
                    }

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }

                // GOLD FOIL
                if (effectDef.id === 'gold_foil') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // Draw as mask
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    if (passType === 'color') {
                        const colorImg = effectDef.textures.diffuse ? effectTextureCache.current.get(effectDef.textures.diffuse) : null;
                        if (colorImg) {
                            sc.drawImage(colorImg, 0, 0, w, h);
                        } else {
                            const grad = sc.createLinearGradient(0, 0, w, h);
                            grad.addColorStop(0, '#FFD700');
                            grad.addColorStop(0.5, '#FFA500');
                            grad.addColorStop(1, '#FFD700');
                            sc.fillStyle = grad;
                            sc.fillRect(0, 0, w, h);
                        }
                    } else if (passType === 'bump') {
                        // Prefer displacement for bump source if available, otherwise normal
                        const bumpSource = effectDef.textures.displacement || effectDef.textures.normal;
                        const bumpImg = bumpSource ? effectTextureCache.current.get(bumpSource) : null;

                        if (bumpImg) {
                            sc.drawImage(bumpImg, 0, 0, w, h);
                        } else {
                            sc.fillStyle = "#C0C0C0";
                            sc.fillRect(0, 0, w, h);
                        }
                    } else if (passType === 'roughness') {
                        const roughImg = effectDef.textures.roughness ? effectTextureCache.current.get(effectDef.textures.roughness) : null;
                        if (roughImg) {
                            sc.drawImage(roughImg, 0, 0, w, h);
                        } else {
                            sc.fillStyle = "#333333";
                            sc.fillRect(0, 0, w, h);
                        }
                    } else if (passType === 'metalness') {
                        const metalImg = effectDef.textures.metalness ? effectTextureCache.current.get(effectDef.textures.metalness) : null;
                        if (metalImg) {
                            sc.drawImage(metalImg, 0, 0, w, h);
                        } else {
                            // Default to high metalness if no texture
                            const mVal = Math.floor((effectDef.materialProps.metalness ?? 1.0) * 255);
                            const hex = mVal.toString(16).padStart(2, '0');
                            sc.fillStyle = `#${hex}${hex}${hex}`;
                            sc.fillRect(0, 0, w, h);
                        }
                    }

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }
                
                // LASER ENGRAVING
                if (effectDef.id === 'laser_engraving') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // 1. Draw Mask (Logo Shape)
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    if (passType === 'color') {
                        // Force Light Silver / Aluminum (Clean Metal Look)
                        // User rejected Dark Grey and Sandpaper.
                        // We want a clean, carved metal look.
                        sc.fillStyle = "#D4D4D4"; 
                        sc.fillRect(0, 0, w, h);

                        // Add Inner Shadow (Depth Simulation) - Quieter, cleaner
                        const { cvs: aoMap, ctx: ao } = getScratchCanvas(w, h);
                        
                        // A. Base (Dark Grey Shadow)
                        ao.drawImage(source, 0, 0, w, h);
                        ao.globalCompositeOperation = "source-in";
                        ao.fillStyle = "#A0A0A0"; // Mid-grey shadow base
                        ao.fillRect(0, 0, w, h);

                        // Noise Injection for realism (Micro-abrasions)
                        const { cvs: noiseMap, ctx: nc } = getScratchCanvas(w, h);
                        // Draw the logo shape
                        nc.drawImage(source, 0, 0, w, h);
                        nc.globalCompositeOperation = "source-in";
                        
                        // Generate Noise
                        const noise = nc.createImageData(w, h);
                        for (let i = 0; i < noise.data.length; i += 4) {
                            const v = Math.random() * 255;
                            noise.data[i] = v;
                            noise.data[i+1] = v;
                            noise.data[i+2] = v;
                            noise.data[i+3] = 40; // Low opacity noise
                        }
                        const tempNoiseCvs = document.createElement('canvas'); // Temp separate canvas
                        tempNoiseCvs.width = w;
                        tempNoiseCvs.height = h;
                        tempNoiseCvs.getContext('2d')?.putImageData(noise, 0, 0);
                        
                        nc.drawImage(tempNoiseCvs, 0, 0);


                        // B. Cutout Center (The "Top" surface) - MUST BE PURE WHITE
                        // Validate: Create a temp canvas with White version of logo
                        const { cvs: cutCvs, ctx: cutCtx } = getScratchCanvas(w, h);
                        cutCtx.drawImage(source, 0, 0, w, h);
                        cutCtx.globalCompositeOperation = "source-in";
                        cutCtx.fillStyle = "#FFFFFF"; // Pure White
                        cutCtx.fillRect(0, 0, w, h);
                        
                        // Draw the White Cutout onto the AO map (shrunk to create edge)
                        ao.globalCompositeOperation = "source-over";
                        ao.save();
                        ao.translate(w/2, h/2);
                        ao.scale(0.97, 0.97); // 3% Border/Edge
                        ao.drawImage(cutCvs, -w/2, -h/2, w, h);
                        ao.restore();
                        
                        // Multiply shading
                        sc.globalCompositeOperation = "multiply";
                        sc.drawImage(aoMap, 0, 0, w, h);

                        // Overlay Noise for Color Texture
                        sc.globalCompositeOperation = "multiply";
                        sc.drawImage(noiseMap, 0, 0, w, h);

                    } else if (passType === 'metalness') {
                         // High Metalness
                         const mVal = Math.floor((effectDef.materialProps.metalness ?? 0.8) * 255);
                         const hex = mVal.toString(16).padStart(2, '0');
                         sc.fillStyle = `#${hex}${hex}${hex}`;
                         sc.fillRect(0, 0, w, h);

                    } else if (passType === 'roughness') {
                         // Base: High Roughness (White)
                         sc.fillStyle = "#FFFFFF";
                         sc.fillRect(0, 0, w, h);

                         const roughImg = effectDef.textures.roughness ? effectTextureCache.current.get(effectDef.textures.roughness) : null;
                         if (roughImg) {
                             // Blend texture softly to keep it mostly matte
                             sc.globalCompositeOperation = "multiply"; 
                             sc.globalAlpha = 0.3; // Only 30% texture influence, keeping it mostly white (rough)
                             sc.drawImage(roughImg, 0, 0, w, h);
                             sc.globalAlpha = 1.0;
                         }



                    } else if (passType === 'bump') {
                         // Negative Bump (Engraving)
                         // White Mask = Low Point
                         sc.fillStyle = "#FFFFFF"; 
                         sc.fillRect(0, 0, w, h);
                    }
                    
                    // FINAL CLIP: Ensure we strictly adhere to the logo alpha channel
                    // This fixes the "Grey Box" issue if the shadow map bled out
                    sc.globalCompositeOperation = "destination-in";
                    sc.drawImage(source, 0, 0, w, h);

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }

                // FABRIC DEBOSSED
                if (effectDef.id === 'fabric_embossed') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // Draw Source (Logo Mask)
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    if (passType === 'color') {
                        // 1. Apply Fabric Texture Overlay
                        // User wants Logo Color * Fabric Texture
                        const fabricTex = effectDef.textures.diffuse ? effectTextureCache.current.get(effectDef.textures.diffuse) : null;

                        // If texture exists, multiply it over the existing logo color
                        if (fabricTex) {
                            sc.globalCompositeOperation = "multiply";
                            sc.drawImage(fabricTex, 0, 0, w, h);

                            // Clean up alpha (multiply usually respects alpha, but ensuring clipping)
                            sc.globalCompositeOperation = "destination-in";
                            sc.drawImage(source, 0, 0, w, h);
                        }

                        // NO Inner Shadow
                    } else if (passType === 'bump') {
                        // Soften the white fill to create sloping walls (Bevel)
                        // White = Low (with negative bumpScale)
                        sc.clearRect(0, 0, w, h);
                        sc.filter = "blur(3px)"; // Soft bevel
                        sc.drawImage(source, 0, 0, w, h);
                        sc.filter = "none";

                        sc.globalCompositeOperation = "source-in";
                        sc.fillStyle = "#FFFFFF";
                        sc.fillRect(0, 0, w, h);

                        // Add fabric displacement texture if available
                        const dispImg = effectDef.textures.displacement ? effectTextureCache.current.get(effectDef.textures.displacement) : null;
                        if (dispImg) {
                            sc.globalCompositeOperation = "multiply";
                            sc.drawImage(dispImg, 0, 0, w, h);
                        }
                    } else if (passType === 'roughness') {
                        const roughImg = effectDef.textures.roughness ? effectTextureCache.current.get(effectDef.textures.roughness) : null;
                        if (roughImg) {
                            sc.drawImage(roughImg, 0, 0, w, h);
                        } else {
                            // Matte
                            sc.fillStyle = "#FFFFFF";
                            sc.fillRect(0, 0, w, h);
                        }
                    }

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }

                // LEATHER DEBOSSED
                if (effectDef.id === 'leather_debossed') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // Draw Source (Logo Mask)
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    if (passType === 'color') {
                        const colorImg = effectDef.textures.diffuse ? effectTextureCache.current.get(effectDef.textures.diffuse) : null;
                        if (colorImg) {
                            sc.drawImage(colorImg, 0, 0, w, h);
                        } else {
                            sc.fillStyle = "#8B4513";
                            sc.fillRect(0, 0, w, h);
                        }

                        // Add Inner Shadow (Fake AO) to simulate depth in color
                        const { cvs: aoMap, ctx: ao } = getScratchCanvas(w, h);
                        ao.save();
                        ao.translate(w / 2, h / 2);
                        ao.scale(0.96, 0.96);
                        ao.filter = "blur(4px)";
                        ao.drawImage(source, -w / 2, -h / 2, w, h);
                        ao.restore();

                        // Center = White (Bright)
                        ao.globalCompositeOperation = "source-in";
                        ao.fillStyle = "white";
                        ao.fillRect(0, 0, w, h);

                        // Edges = Dark Gray (Shadow)
                        ao.globalCompositeOperation = "destination-over";
                        ao.fillStyle = "#303030"; // Very Dark Gray for deep press illusion
                        ao.fillRect(0, 0, w, h);

                        // Multiply shading onto leather color
                        sc.globalCompositeOperation = "multiply";
                        sc.drawImage(aoMap, 0, 0, w, h);

                        // CRITICAL: Trim the overflow!
                        // The aoMap has a solid gray background which bleeds into transparency.
                        // We must cut it back to the logo shape.
                        sc.globalCompositeOperation = "destination-in";
                        sc.drawImage(source, 0, 0, w, h);

                    } else if (passType === 'bump') {
                        // Soften the white fill to create sloping walls for the deboss
                        // White = Low (with negative bumpScale)
                        sc.clearRect(0, 0, w, h);
                        sc.filter = "blur(3px)"; // Soft bevel
                        sc.drawImage(source, 0, 0, w, h);
                        sc.filter = "none";

                        // Solid White Bevel
                        sc.globalCompositeOperation = "source-in";
                        sc.fillStyle = "#FFFFFF";
                        sc.fillRect(0, 0, w, h);

                        const dispImg = effectDef.textures.displacement ? effectTextureCache.current.get(effectDef.textures.displacement) : null;
                        if (dispImg) {
                            sc.globalCompositeOperation = "multiply";
                            sc.drawImage(dispImg, 0, 0, w, h);
                        }
                    } else if (passType === 'roughness') {
                        const roughImg = effectDef.textures.roughness ? effectTextureCache.current.get(effectDef.textures.roughness) : null;
                        if (roughImg) {
                            sc.drawImage(roughImg, 0, 0, w, h);
                        } else {
                            sc.fillStyle = "#666666";
                            sc.fillRect(0, 0, w, h);
                        }
                    }

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }



                // EMBROIDERY
                if (effectDef.id === 'embroidery') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    sc.drawImage(source, 0, 0, w, h);

                        // Helper to generate static noise
                        const createNoisePattern = (ctx: CanvasRenderingContext2D) => {
                             const noiseCvs = document.createElement('canvas');
                             noiseCvs.width = 256;
                             noiseCvs.height = 256;
                             const nCtx = noiseCvs.getContext('2d');
                             if (!nCtx) return null;
                             
                             const idata = nCtx.createImageData(256, 256);
                             const buffer = new Uint32Array(idata.data.buffer);
                             for (let i = 0; i < buffer.length; i++) {
                                 // High frequency noise with some alpha variance
                                 const val = (Math.random() < 0.5 ? 0 : 255);
                                 // ABGR (little endian): Alpha=var, B=val, G=val, R=val
                                 buffer[i] = (Math.floor(Math.random() * 155 + 100) << 24) | (val << 16) | (val << 8) | val;
                             }
                             nCtx.putImageData(idata, 0, 0);
                             return ctx.createPattern(noiseCvs, 'repeat');
                        };

                        if (passType === 'color') {
                            // -1. Micro-Fraying "Fuzz" (New Feature)
                            // Create a fuzzy, fibrous halo behind the main threads
                             const { cvs: fuzzLayer, ctx: fc } = getScratchCanvas(w, h);
                             
                             // Draw the base shape expanded and blurred
                             fc.save();
                             fc.translate(w/2, h/2);
                             fc.scale(1.02, 1.02); // 2% Expansion for fraying
                             fc.filter = "blur(2px)"; // Soften the edge
                             fc.drawImage(source, -w/2, -h/2, w, h);
                             fc.restore();

                             // Apply Noise to make it fibrous (cut holes in the blur)
                             const noisePat = createNoisePattern(fc);
                             if (noisePat) {
                                 fc.globalCompositeOperation = "source-in";
                                 fc.fillStyle = noisePat;
                                 fc.fillRect(0, 0, w, h);
                             }

                             // Composite the Fuzz BEHIND the main logo
                             sc.save();
                             sc.globalCompositeOperation = "destination-over";
                             sc.globalAlpha = 0.6; // Subtle fuzz
                             sc.drawImage(fuzzLayer, 0, 0, w, h);
                             sc.restore();

                        // 0. Drop Shadow (Simulate Thickness/Lift)
                        // Draw a blurred black shadow BEHIND the logo to make it look like it's "sitting on top"
                        sc.save();
                        sc.globalCompositeOperation = "destination-over"; // Draw behind existing content
                        sc.shadowColor = "rgba(0,0,0,0.7)"; // Deep opacity for thickness intensity
                        
                        let shadowBlur = 20; 
                        let shadowY = 15;

                        if (currentModel === 'cap') {
                             shadowBlur = 35; // Softer, wider shadow for 3D puff
                             shadowY = 25; // Higher lift
                        }

                        sc.shadowBlur = shadowBlur; 
                        sc.shadowOffsetX = 0;
                        sc.shadowOffsetY = 10000 + shadowY; // Vertical offset +15px for visible float/thickness
                        // Draw off-screen so only the shadow texture hits the canvas
                        sc.drawImage(source, 0, -10000, w, h);
                        sc.restore();

                        const colorImg = effectDef.textures.diffuse ? effectTextureCache.current.get(effectDef.textures.diffuse) : null;
                        if (colorImg) {
                            sc.globalCompositeOperation = "multiply";
                            sc.drawImage(colorImg, 0, 0, w, h);
                        }

                        // --- Fake 3D Shading (Simulated Curvature/AO) ---
                        // Create a specific Light/Shadow map to force "roundness" in the color channel
                        // This prevents flatness even if the lighting angle is perpendicular
                        const { cvs: lightMap, ctx: lm } = getScratchCanvas(w, h);

                        // 1. Create the Inner White Shape (High Point)
                        lm.save();
                        lm.translate(w / 2, h / 2);
                        lm.scale(0.97, 0.97); // Shrink 3% for tighter bevel
                        lm.filter = "blur(5px)"; // Sharper rounded edge
                        lm.drawImage(source, -w / 2, -h / 2, w, h);
                        lm.filter = "none";
                        lm.restore();

                        // Convert to Pure White Mask
                        lm.globalCompositeOperation = "source-in";
                        lm.fillStyle = "#FFFFFF";
                        lm.fillRect(0, 0, w, h);

                        // 2. Fill Background (Edges) with Gray (Shadow)
                        // Using 'destination-over' puts gray BEHIND the white blur
                        // Result: Center = White (1.0 brightness), Edges fade to Gray (Lighter)
                        lm.globalCompositeOperation = "destination-over";
                        lm.fillStyle = "#C0C0C0"; // Subtle Shadow (Light Grey) to avoid "dirty" look
                        lm.fillRect(0, 0, w, h);

                        // 3. Multiply this Shading onto the Main Color
                        sc.globalCompositeOperation = "multiply";
                        sc.drawImage(lightMap, 0, 0, w, h);
                    } else if (passType === 'bump') {
                        // Create Puffy "Inner Bevel" by shrinking the high-point and blurring
                        // This ensures the slope starts LOW at the edge and rises UP (Convex)
                        sc.clearRect(0, 0, w, h);

                        sc.save();
                        sc.translate(w / 2, h / 2);
                        sc.scale(0.97, 0.97); // Match color pass 
                        sc.filter = "blur(5px)"; // Match color pass
                        // Draw centered due to translation
                        sc.drawImage(source, -w / 2, -h / 2, w, h);
                        sc.restore();

                        // Fill with White (preserving alpha/blur)
                        sc.globalCompositeOperation = "source-in";
                        sc.fillStyle = "#FFFFFF";
                        sc.fillRect(0, 0, w, h);

                        // Add subtle texture to the height map so it's not a perfect plastic block
                        // Using roughness map as proxy for thread height details
                        const detailImg = effectDef.textures.roughness ? effectTextureCache.current.get(effectDef.textures.roughness) : null;
                        if (detailImg) {
                            sc.globalCompositeOperation = "multiply";
                            sc.globalAlpha = 0.5; // Moderate texture influence
                            sc.drawImage(detailImg, 0, 0, w, h);
                            sc.globalAlpha = 1.0;
                        }
                    } else if (passType === 'roughness') {
                        sc.globalCompositeOperation = "source-in";
                        // Force ABSOLUTE Matte finish (Max Roughness) to prevent ANY shine
                        // Ignoring texture for now as it might be too dark/shiny
                        sc.fillStyle = "#FFFFFF"; // 1.0 Roughness
                        sc.fillRect(0, 0, w, h);
                    } else if (passType === 'metalness') {
                        sc.globalCompositeOperation = "source-in";
                        const mVal = Math.floor((effectDef.materialProps.metalness || 0) * 255);
                        const hex = mVal.toString(16).padStart(2, '0');
                        sc.fillStyle = `#${hex}${hex}${hex}`;
                        sc.fillRect(0, 0, w, h);
                    } else if (passType === 'normal') {
                        sc.globalCompositeOperation = "source-in";
                        const normalImg = effectDef.textures.normal ? effectTextureCache.current.get(effectDef.textures.normal) : null;
                        if (normalImg) {
                            sc.drawImage(normalImg, 0, 0, w, h);
                        } else {
                            // Flat normal for transparency/default
                            sc.fillStyle = "#8080FF";
                            sc.fillRect(0, 0, w, h);
                        }
                    }

                    sc.globalCompositeOperation = "destination-in";
                    sc.drawImage(source, 0, 0, w, h);

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }

                // GLASS ETCHING (White Frost Effect)
                if (effectDef.id === 'glass_etching') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);

                    // 1. Draw Base Shape (to establish alpha)
                    sc.drawImage(source, 0, 0, w, h);

                    if (passType === 'color') {
                        // 2. Force White (Etched glass is always white/frosted, ignoring ink color)
                        sc.globalCompositeOperation = "source-in";
                        sc.fillStyle = "#FFFFFF"; // Pure White Base
                        sc.fillRect(0, 0, w, h);

                        // 3. Apply "Frost" Texture (Grain)
                        // This adds the "sandblasted" noise look
                        const frostImg = effectDef.textures.diffuse ? effectTextureCache.current.get(effectDef.textures.diffuse) : null;
                        if (frostImg) {
                            sc.globalCompositeOperation = "multiply";
                            sc.drawImage(frostImg, 0, 0, w, h);
                        }

                        // 4. Reduce Opacity slightly (Glass etching is semi-translucent)
                        // But since we are modifying the pixel data directly, we can't just set globalAlpha on drawImage easily
                        // unless we redraw it.
                        // Actually, 'source-in' preserved the alpha of 'source'. 
                        // Let's multiply alpha by 0.85
                        sc.globalCompositeOperation = "destination-in";
                        sc.globalAlpha = 0.85; // 85% Opacity
                        sc.drawImage(source, 0, 0, w, h);
                        sc.globalAlpha = 1.0;
                    } 
                    // Use Standard logic for Bump/Roughness blocks via fall-through? 
                    // No, invalid structure. We must handle other passes or return.
                    // For Glass Etching, the Standard Bump/Roughness logic below works fine 
                    // BUT we need to output the canvas here.
                    
                    else if (passType === 'bump') {
                         // Standard Bump Logic duplication for now, or just let it fall through?
                         // If we break here, we must handle it.
                         // Let's implement specific bump handling or delegate.
                         // Glass Etching uses Negative Bump (Engraving).
                         // The Standard Bump block (below) handles negative bump correctly (#404040).
                         // So we can arguably just return specific content for COLOR, and let others use standard?
                         // The structure of this loop is: if (special) { handle; return; }
                         // So we MUST handle all passes here.
                         
                         // Re-draw source for bump pass
                         sc.clearRect(0,0,w,h);
                         sc.drawImage(source, 0, 0, w, h);
                         sc.globalCompositeOperation = "source-in";
                         const bumpValue = (effectDef.materialProps.bumpScale || 0) > 0 ? "#FFFFFF" : "#404040";
                         sc.fillStyle = bumpValue;
                         sc.fillRect(0, 0, w, h);
                    }
                    else if (passType === 'roughness') {
                        // Max Roughness (Sandblasted)
                         sc.clearRect(0,0,w,h);
                         sc.drawImage(source, 0, 0, w, h);
                         sc.globalCompositeOperation = "source-in";
                         // Use config value (usually 1.0)
                         const roughnessValue = Math.floor((effectDef.materialProps.roughness || 1.0) * 255);
                         const hex = roughnessValue.toString(16).padStart(2, '0');
                         sc.fillStyle = `#${hex}${hex}${hex}`;
                         sc.fillRect(0, 0, w, h);
                    }

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    return;
                }

                // PAD PRINT (Custom Textured)
                if (effectDef.id === 'pad_print') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    // Generate Subtle Grain (Orange Peel / Paper texture)
                    const { cvs: noiseMap } = getScratchCanvas(w, h);
                    const nc = noiseMap.getContext('2d');
                    if (nc) {
                        const idata = nc.createImageData(w, h);
                        for (let i = 0; i < idata.data.length; i += 4) {
                            const val = Math.random() * 255;
                            idata.data[i] = val;
                            idata.data[i+1] = val;
                            idata.data[i+2] = val;
                            idata.data[i+3] = 40; // Low opacity noise
                        }
                        nc.putImageData(idata, 0, 0);
                    }

                    if (passType === 'color') {
                        // Standard Color
                        ctx.drawImage(source, -w/2, -h/2, w, h);
                        
                        // Overlay very faint noise to break "digital flat" look
                        ctx.save();
                        ctx.translate(-w/2, -h/2);
                        ctx.globalCompositeOperation = "multiply";
                        ctx.globalAlpha = 0.05; // Extremely faint
                        ctx.drawImage(noiseMap, 0, 0);
                        ctx.restore();

                    } else if (passType === 'bump') {
                        // White Base (High)
                        sc.fillStyle = "#FFFFFF";
                        sc.fillRect(0, 0, w, h);

                        // Overlay Noise for surface texture
                        sc.globalCompositeOperation = "multiply";
                        sc.globalAlpha = 0.2; // 20% surface variation
                        sc.drawImage(noiseMap, 0, 0);

                        ctx.drawImage(cvs, -w / 2, -h / 2, w, h);

                    } else if (passType === 'roughness') {
                         // Matte Base
                         const rVal = Math.floor((effectDef.materialProps.roughness || 0.9) * 255);
                         const hex = rVal.toString(16).padStart(2, '0');
                         sc.fillStyle = `#${hex}${hex}${hex}`;
                         sc.fillRect(0, 0, w, h);

                         // Add noise to roughness so the light hits it unevenly
                         sc.globalCompositeOperation = "multiply";
                         sc.globalAlpha = 0.15;
                         sc.drawImage(noiseMap, 0, 0);

                         ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                    } else if (passType === 'metalness') {
                         sc.fillStyle = "#000000";
                         sc.fillRect(0, 0, w, h);
                         ctx.drawImage(cvs, -w/2, -h/2, w, h);
                    }
                    return;
                }

                // STANDARD / EMBOSS / OTHER
                if (passType === 'color') {
                    ctx.drawImage(source, -w / 2, -h / 2, w, h);
                } else if (passType === 'bump') {
                    if (!effectDef.materialProps.bumpScale || effectDef.materialProps.bumpScale === 0) {
                        return;
                    }

                    const { cvs, ctx: sc } = getScratchCanvas(w, h);
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    const bumpValue = effectDef.materialProps.bumpScale > 0 ? "#FFFFFF" : "#404040";
                    sc.fillStyle = bumpValue;
                    sc.fillRect(0, 0, w, h);

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                } else if (passType === 'roughness') {
                    const { cvs, ctx: sc } = getScratchCanvas(w, h);
                    sc.drawImage(source, 0, 0, w, h);
                    sc.globalCompositeOperation = "source-in";

                    const roughnessValue = Math.floor((effectDef.materialProps.roughness || 0.5) * 255);
                    const hex = roughnessValue.toString(16).padStart(2, '0');
                    sc.fillStyle = `#${hex}${hex}${hex}`;
                    sc.fillRect(0, 0, w, h);

                    ctx.drawImage(cvs, -w / 2, -h / 2, w, h);
                }
            };

            // MERGE & SORT for Layering (Oldest First = Bottom Layer)
            const allItems = sortInstancesByLayerOrder(areaLogos, areaTexts, 'asc');

            // UNIFIED RENDER LOOP
            for (const item of allItems) {
                if (item.type === 'logo') {
                    // --- DRAW LOGO ---
                    const instance = item.data;
                    const logoDef = logos.find(l => l.id === instance.logoId);
                    if (!logoDef) continue;

                    const img = await loadImage(logoDef.processedImage);
                    const effectDef = (Effects as any)[instance.effect] || Effects.none;

                    // --- VISUAL BACKFILL (Square Pen Fix) ---
                    // Disabled: User requested that irrespective of logo background, it shouldn't change pen's color.
                    /*
                    // Sample pixel at (0,0) to determine Fill Color, apply to underlying Body mesh
                    if (currentModel === 'square-pen' && printArea.parentMesh) {
                        try {
                            const tempCvs = document.createElement('canvas');
                            tempCvs.width = 1; tempCvs.height = 1;
                            const tCtx = tempCvs.getContext('2d');
                            if (tCtx) {
                                tCtx.drawImage(img, 0, 0, 1, 1);
                                const pData = tCtx.getImageData(0,0,1,1).data;
                                // Only apply if the corner pixel is relatively opaque
                                if (pData[3] > 50) {
                                    const bodyColor = new THREE.Color(`rgb(${pData[0]}, ${pData[1]}, ${pData[2]})`);
                                    printArea.parentMesh.traverse((child: any) => {
                                        if (child.isMesh && child.name.toLowerCase().includes('body') && child.material) {
                                            const materials = Array.isArray(child.material) ? child.material : [child.material];
                                            materials.forEach((m: any) => {
                                                if ('color' in m) m.color.copy(bodyColor);
                                            });
                                        }
                                    });
                                }
                            }
                        } catch (e) {
                            console.warn("Could not extract fill color for visual backfill", e);
                        }
                    }
                    */

                    // Calculate transforms
                    // If this instance belongs to 'all-faces' but we're rendering an individual print area,
                    // remap the normalized all-faces position into the area's UV bounds so the logo appears
                    // at the same relative position on each area.
                    let posX = instance.position.x;
                    let posY = instance.position.y;

                    if (instance.printAreaId === 'all-faces' && printArea.id !== 'all-faces' && printArea.uvBounds) {
                        const ub = printArea.uvBounds;
                        const areaW = (ub.maxU - ub.minU) || 1;
                        const areaH = (ub.maxV - ub.minV) || 1;
                        // Map normalized all-faces pos into this area's UV window
                        posX = ((ub.minU + posX * areaW) % 1 + 1) % 1;
                        posY = ((ub.minV + posY * areaH) % 1 + 1) % 1;
                    }

                    // SPECIAL FIX: Cap model coordinate interpretation
                    // Apply inversions based on print area to fix 3D positioning
                    let finalPosX = posX;
                    let finalPosY = posY;
                    
                    if (currentModel === 'cap') {
                        // FIX DIRECTION: Invert Y for ALL Cap areas so Drag Up -> 3D Up
                        // Note: Inversion flips across 0.5. If true center is not 0.5, this causes a shift.
                        // Note: Inversion flips across 0.5. If true center is not 0.5, this causes a shift.
                        // Note: Inversion flips across 0.5. If true center is not 0.5, this causes a shift.
                        // Adjusted offset based on user data: 
                        // Back: 0.25 -> 0.24 (Diff -0.5)
                        // Back Strip: 0.10 -> 0.09 (Diff -0.81)
                        // Front: 0.22 -> 0.17 (Diff -0.61)
                        // Left/Right: 0.36 -> 0.37/0.35 (Diff -0.28)
                        let CAP_Y_OFFSET = -0.5; // Default for Back
                        
                        const printAreaNameLower = printArea.name.toLowerCase();
                        if (printAreaNameLower.includes('strip')) {
                             CAP_Y_OFFSET = -0.81;
                        } else if (printAreaNameLower.includes('front')) {
                             CAP_Y_OFFSET = -0.61;
                        } else if (printAreaNameLower.includes('left') || printAreaNameLower.includes('right')) {
                             CAP_Y_OFFSET = -0.28;
                        }

                        finalPosY = (1 - posY) + CAP_Y_OFFSET;

                        // Only Back Strip needs X inversion
                        // Y axis is handled globally by invertY: true in config
                        const printAreaName = printArea.name.toLowerCase();
                        if (printAreaName.includes('back') && printAreaName.includes('strip')) {
                             finalPosX = 1 - posX;
                        }
                    } else if (currentModel === 'diary') {
                        // Fix Diary: 2D Center (0.5) -> 3D Center (0.44) => Offset -0.06
                        finalPosY = posY - 0.18;
                    } else if (currentModel === 'polo-tshirt' && printArea.name.toLowerCase().includes('back')) {
                        // Fix Polo Back: 2D Center (0.5) -> 3D Center (0.32) => Offset -0.18
                        finalPosY = posY - 0.33;
                    } else if (currentModel === 'dye-sublimated-lanyard-with-metal-crimp') {
                        // Fix Lanyard: 2D Center (0.5) -> 3D Center (~0.7) => Offset +0.20
                        finalPosY = posY + 0.45;
                    }



                    const centerX = (useInvertX
                        ? (1 - finalPosX)
                        : finalPosX) * 1024;

                    const centerY = (useInvertY
                        ? finalPosY           // Inverted Y (e.g. for Towel 1, Pen)
                        : (1 - finalPosY)) * 1024; // Standard Y


                    // Base size 300px * scale
                    const baseW = 300 * instance.scale;
                    const baseH = (baseW / img.width) * img.height;
                    
                    // Rotation Fix: 
                    // The texture rendering uses ctx.scale(..., -1) to flip Y for Three.js UV compatibility.
                    // This single-axis flip reverses the rotation direction (Clockwise becomes Counter-Clockwise).
                    // However, if useTextureFlipX is TRUE, we often scale(-1, -1). A double-flip restores the rotation direction.
                    // Therefore:
                    // - If FlipX is TRUE: Rotation is Normal.
                    // - If FlipX is FALSE: Rotation is Inverted (needs correction).
                    const rotationRad = (instance.rotation * Math.PI) / 180;
                    const rotation = useTextureFlipX ? rotationRad : -rotationRad;

                    // Super-sampled canvas for effect processing
                    const ssaa = 2;
                    const w = Math.ceil(baseW) * ssaa;
                    const h = Math.ceil(baseH) * ssaa;

                    // Tiling Logic for Sublimation on Towel
                    const isTiled = currentModel === 'towel' && instance.effect === 'sublimation';

                    const drawInstance = (ctx: CanvasRenderingContext2D, passType: 'color' | 'bump' | 'roughness' | 'metalness' | 'normal') => {
                        if (isTiled) {
                            // --- TILED RENDERING ---
                            const { cvs: tileCvs, ctx: tileCtx } = getScratchCanvas(w, h);
                            
                            // Draw centered tile (RAW IMAGE - No per-tile effect)
                            tileCtx.translate(w / 2, h / 2);
                            // Match Standard Rendering: Apply Flip X if needed, AND Flip Y (Standard Three.js/Canvas mismatch)
                            tileCtx.scale(useTextureFlipX ? -1 : 1, -1);
                            
                            // Draw raw image directly to tile
                            tileCtx.drawImage(img, -w/2, -h/2, w, h);

                            // Create Pattern from Raw Tile
                            const pattern = ctx.createPattern(tileCvs, 'repeat');
                            if (pattern) {
                                ctx.save();
                                // Pattern Transform
                                // Use centerX/centerY to allow Dragon (shifting the pattern)
                                ctx.translate(centerX, centerY); 
                                ctx.rotate(rotation);
                                // The pattern origin is now at centerX,centerY. 
                                // We don't strictly need to offset back by 512 unless we want a specific pivot, usually 0,0 of tile is at center.
                                // But since it's a repeating pattern, just moving the origin is enough to "Slide" the texture.
                                
                                ctx.scale(1 / ssaa, 1 / ssaa); 
                                
                                ctx.fillStyle = pattern;
                                // Fill huge area relative to the new origin
                                ctx.fillRect(-5000, -5000, 10000, 10000); 
                                
                                // --- APPLY GLOBAL SUBLIMATION TEXTURE ---
                                // Apply the fabric texture over the ENTIRE pattern continuously
                                if (passType === 'color' && originalMapImageRef.current) {
                                    // 1. Multiply Fabric Texture
                                    ctx.globalCompositeOperation = 'multiply';
                                    
                                    // Reset transform to draw texture statically on the canvas (0,0)
                                    ctx.setTransform(1, 0, 0, 1, 0, 0); 
                                    ctx.drawImage(originalMapImageRef.current, 0, 0, 1024, 1024);
                                    
                                    // 2. Clip back to Pattern Alpha
                                    ctx.globalCompositeOperation = 'destination-in';
                                    
                                    // Re-apply transform for the mask to match the pattern's position
                                    ctx.translate(centerX, centerY); 
                                    ctx.rotate(rotation);
                                    ctx.scale(1 / ssaa, 1 / ssaa); 
                                    
                                    ctx.fillStyle = pattern;
                                    ctx.fillRect(-5000, -5000, 10000, 10000);
                                }
                                
                                ctx.restore();
                            }
                        } else {
                            // --- STANDARD RENDERING ---
                            ctx.save();
                            ctx.translate(centerX, centerY);
                            ctx.rotate(rotation);
                            ctx.scale(useTextureFlipX ? -1 : 1, -1); // Flip for correct orientation
                            ctx.scale(1 / ssaa, 1 / ssaa);
                            applyEffect(ctx, img, w, h, effectDef, passType);
                            ctx.restore();
                        }
                    };

                    drawInstance(colorCtx, 'color');
                    drawInstance(bumpCtx, 'bump');
                    drawInstance(roughCtx, 'roughness');
                    drawInstance(metalnessCtx, 'metalness');
                    drawInstance(normalCtx, 'normal');

                } else {
                    // --- DRAW TEXT ---
                    const instance = item.data;
                    const effectDef = (Effects as any)[instance.effect] || Effects.none;

                    // 1. Calculate Text Dimensions & Style
                    const ssaa = 2;
                    // High multiplier for sharp text
                    // Adjusted from 4 to 2.2 to match visual size in 2D editor (approx 465px width) due to fixed 1024px texture
                    const fontSize = instance.fontSize * instance.scale * 2.2;
                    const lineHeight = fontSize * 1.2;
                    const lines = instance.text.split('\n');

                    // Filter out 'underline' from font string (it breaks canvas font parsing causing tiny default text)
                    const validStyles = instance.fontStyle.filter((s: string) => s !== 'underline');
                    const hasUnderline = instance.fontStyle.includes('underline');

                    const font = `${validStyles.join(' ')} ${fontSize}px ${instance.fontFamily}`;

                    // Measure max width
                    let maxWidth = 0;
                    colorCtx.save();
                    colorCtx.font = font;
                    lines.forEach((line: string) => {
                        const m = colorCtx.measureText(line);
                        if (m.width > maxWidth) maxWidth = m.width;
                    });
                    colorCtx.restore();

                    const totalHeight = lines.length * lineHeight;
                    const padding = fontSize * 0.5;
                    const w = Math.ceil(maxWidth + padding * 2) * ssaa;
                    const h = Math.ceil(totalHeight + padding * 2) * ssaa;

                    // 2. Render Text to Scratch Canvas
                    const { cvs: textCvs, ctx: textCtx } = getScratchCanvas(w, h);

                    textCtx.scale(ssaa, ssaa);
                    textCtx.font = font;
                    textCtx.fillStyle = instance.color;
                    const align = instance.textAlign || 'center';
                    textCtx.textAlign = align;
                    textCtx.textBaseline = 'middle';

                    // Center in the scratch canvas
                    const centerX_local = (w / ssaa) / 2;
                    const startY_local = ((h / ssaa) - totalHeight) / 2 + (lineHeight / 2);

                    let drawX = centerX_local;
                    if (align === 'left') drawX = padding;
                    else if (align === 'right') drawX = (w / ssaa) - padding;

                    lines.forEach((line: string, i: number) => {
                        const y = startY_local + (i * lineHeight);
                        textCtx.fillText(line, drawX, y);

                        if (hasUnderline) {
                            const m = textCtx.measureText(line);
                            const lineW = m.width;
                            const lineY = y + (fontSize * 0.4); // Position underline below text
                            const thickness = Math.max(2, fontSize * 0.08); // Responsive thickness

                            let uX = drawX - (lineW / 2);
                            if (align === 'left') uX = drawX;
                            else if (align === 'right') uX = drawX - lineW;

                            textCtx.fillRect(uX, lineY, lineW, thickness);
                        }
                    });

                    // 3. Render Scratch Canvas using applyEffect
                    // Remap all-faces text positions into individual print area UV bounds
                    let posX = instance.position.x;
                    let posY = instance.position.y;
                    if (instance.printAreaId === 'all-faces' && printArea.id !== 'all-faces' && printArea.uvBounds) {
                        const ub = printArea.uvBounds;
                        const areaW = (ub.maxU - ub.minU) || 1;
                        const areaH = (ub.maxV - ub.minV) || 1;
                        posX = ((ub.minU + posX * areaW) % 1 + 1) % 1;
                        posY = ((ub.minV + posY * areaH) % 1 + 1) % 1;
                    }

                    // SPECIAL FIX: Cap model coordinate interpretation for text
                    // Apply inversions based on print area to fix 3D positioning
                    let finalPosX = posX;
                    let finalPosY = posY;
                    
                    if (currentModel === 'cap') {
                        // FIX DIRECTION: Invert Y for ALL Cap areas so Drag Up -> 3D Up
                        // Note: Inversion flips across 0.5. If true center is not 0.5, this causes a shift.
                        // Note: Inversion flips across 0.5. If true center is not 0.5, this causes a shift.
                        // Note: Inversion flips across 0.5. If true center is not 0.5, this causes a shift.
                        // Adjusted offset based on user data: 
                        // Back: Diff -0.5
                        // Back Strip: Diff -0.81
                        // Front: Diff -0.61
                        // Left/Right: Diff -0.28
                        let CAP_Y_OFFSET = -0.5;
                        
                        const printAreaNameLower = printArea.name.toLowerCase();
                        if (printAreaNameLower.includes('strip')) {
                             CAP_Y_OFFSET = -0.81;
                        } else if (printAreaNameLower.includes('front')) {
                             CAP_Y_OFFSET = -0.61;
                        } else if (printAreaNameLower.includes('left') || printAreaNameLower.includes('right')) {
                             CAP_Y_OFFSET = -0.28;
                        }

                        finalPosY = (1 - posY) + CAP_Y_OFFSET;

                        // Only Back Strip needs X inversion
                        // Y axis is handled globally by invertY: true in config
                        const printAreaName = printArea.name.toLowerCase();
                        if (printAreaName.includes('back') && printAreaName.includes('strip')) {
                             finalPosX = 1 - posX;
                        }
                    } else if (currentModel === 'diary') {
                        // Fix Diary: 2D Center (0.5) -> 3D Center (0.44) => Offset -0.06 -> Updated to -0.18
                        finalPosY = posY - 0.18;
                    } else if (currentModel === 'polo-tshirt' && printArea.name.toLowerCase().includes('back')) {
                        // Fix Polo Back: 2D Center (0.5) -> 3D Center (0.32) => Offset -0.18 -> Updated to -0.33
                        finalPosY = posY - 0.33;
                    } else if (currentModel === 'dye-sublimated-lanyard-with-metal-crimp') {
                        // Fix Lanyard: 2D Center (0.5) -> 3D Center (~0.7) => Offset +0.20 -> Updated to +0.45
                        finalPosY = posY + 0.45;
                    }

                    const cx = (useInvertX
                        ? (1 - finalPosX)
                        : finalPosX) * 1024;

                    const cy = (useInvertY
                        ? finalPosY
                        : (1 - finalPosY)) * 1024;

                    const drawInstance = (ctx: CanvasRenderingContext2D, passType: 'color' | 'bump' | 'roughness' | 'metalness' | 'normal') => {
                        ctx.save();
                        ctx.translate(cx, cy);
                        
                        // Apply Rotation Fix for Text too
                        const textRotRad = (instance.rotation * Math.PI) / 180;
                        ctx.rotate(useTextureFlipX ? textRotRad : -textRotRad);
                        
                        ctx.scale(useTextureFlipX ? -1 : 1, -1);

                        ctx.scale(1 / ssaa, 1 / ssaa);

                        applyEffect(ctx, textCvs, w, h, effectDef, passType);

                        ctx.restore();
                    };

                    drawInstance(colorCtx, 'color');
                    drawInstance(bumpCtx, 'bump');
                    drawInstance(roughCtx, 'roughness');
                    drawInstance(metalnessCtx, 'metalness');
                    drawInstance(normalCtx, 'normal');
                }
            }

            // Update Textures
            const applyTextureSettings = (tex: THREE.CanvasTexture) => {
                tex.colorSpace = THREE.SRGBColorSpace;

                // Use configured texture flip
                tex.flipY = useTextureFlip;

                // Use simple 1:1 mapping to preserve logo aspect ratio
                // UV bounds scaling was causing logo distortion
                tex.repeat.set(1, 1);
                tex.offset.set(0, 0);
                
                // Use RepeatWrapping for all-faces wrap-around, ClampToEdge for others
                if (printArea.id === 'all-faces') {
                    tex.wrapS = THREE.RepeatWrapping;
                    tex.wrapT = THREE.RepeatWrapping;
                } else {
                    tex.wrapS = THREE.ClampToEdgeWrapping;
                    tex.wrapT = THREE.ClampToEdgeWrapping;
                }
            };

            if (canvasRef.current && !textureRef.current) {
                textureRef.current = new THREE.CanvasTexture(canvasRef.current);
                applyTextureSettings(textureRef.current);
            } else if (textureRef.current) {
                applyTextureSettings(textureRef.current);
                textureRef.current.needsUpdate = true;
            }

            if (bumpCanvasRef.current && !bumpTextureRef.current) {
                bumpTextureRef.current = new THREE.CanvasTexture(bumpCanvasRef.current);
                applyTextureSettings(bumpTextureRef.current);
            } else if (bumpTextureRef.current) {
                applyTextureSettings(bumpTextureRef.current);
                bumpTextureRef.current.needsUpdate = true;
            }

            if (roughnessCanvasRef.current && !roughnessTextureRef.current) {
                roughnessTextureRef.current = new THREE.CanvasTexture(roughnessCanvasRef.current);
                applyTextureSettings(roughnessTextureRef.current);
            } else if (roughnessTextureRef.current) {
                applyTextureSettings(roughnessTextureRef.current);
                roughnessTextureRef.current.needsUpdate = true;
            }

            if (metalnessCanvasRef.current && !metalnessTextureRef.current) {
                metalnessTextureRef.current = new THREE.CanvasTexture(metalnessCanvasRef.current);
                applyTextureSettings(metalnessTextureRef.current);
            } else if (metalnessTextureRef.current) {
                applyTextureSettings(metalnessTextureRef.current);
                metalnessTextureRef.current.needsUpdate = true;
            }

            if (normalCanvasRef.current && !normalTextureRef.current) {
                normalTextureRef.current = new THREE.CanvasTexture(normalCanvasRef.current);
                applyTextureSettings(normalTextureRef.current);
            } else if (normalTextureRef.current) {
                applyTextureSettings(normalTextureRef.current);
                normalTextureRef.current.needsUpdate = true;
            }

            // Apply to Material
            if (printArea.meshRef && printArea.meshRef.material) {
                const mat = printArea.meshRef.material as THREE.MeshStandardMaterial;

                // If this is the merged 'all-faces' wrapper mesh, keep it invisible in 3D.
                // The single `all-faces` instance is used only for 2D editing; individual
                // per-area meshes receive the remapped texture so the logo appears on each.
                if (printArea.id === 'all-faces' && currentModel !== 'square-pen') {
                    mat.map = null;
                    mat.bumpMap = null;
                    mat.normalMap = originalNormalMapRef.current;
                    mat.roughnessMap = originalRoughnessMapRef.current;
                    mat.opacity = 0;
                    mat.transparent = true;
                    mat.depthWrite = false;
                    mat.needsUpdate = true;
                } else {
                    // Only show mesh if there are logos or text OR if there is a background color
                    const hasContent = areaLogos.length > 0 || areaTexts.length > 0 || !!printArea.backgroundColor;
                    mat.opacity = hasContent ? 1 : 0;
                    mat.depthWrite = hasContent;

                    if (textureRef.current) {
                        mat.map = textureRef.current;
                    }
                    
                    // Opaque by default to prevent sub-pixel background bleed at seams
                    mat.transparent = false;
                    
                    // If the original material or logo uses alpha, force a hard edge
                    mat.alphaTest = 0.5;

                    const activeLogo = areaLogos.length > 0 ? areaLogos[0] : null;
                    const activeEffect = activeLogo ? activeLogo.effect : (areaTexts.length > 0 ? areaTexts[0].effect : 'none');

                    if (activeEffect === 'sublimation') {
                        mat.bumpMap = null;
                        mat.normalMap = originalNormalMapRef.current;
                        mat.roughnessMap = originalRoughnessMapRef.current;
                        mat.needsUpdate = true;
                    } else {
                        mat.bumpMap = bumpTextureRef.current;
                        const def = (Effects as any)[activeEffect] || Effects.none;
                        mat.bumpScale = def.materialProps.bumpScale ?? 0;

                        mat.roughnessMap = roughnessTextureRef.current;
                        mat.roughness = 1.0; // ALWAYS 1.0 so the Roughness Map controls the value per-pixel

                        mat.metalnessMap = metalnessTextureRef.current;
                        mat.metalness = 1.0; // ALWAYS 1.0 so the Metalness Map controls the value per-pixel

                        // Switch Normal Map for Embroidery
                        if (activeEffect === 'embroidery') {
                            mat.normalMap = normalTextureRef.current;
                            mat.normalScale.set(2, 2);
                        } else {
                            mat.normalMap = originalNormalMapRef.current;
                            mat.normalScale.set(1, 1);
                        }

                        mat.needsUpdate = true;
                    }
                }
            }
        };

        // Run Render
        render();

    }, [printArea, logoInstances, textInstances, logos, printArea.borrowedDiffuseTexture, fontsReady]);

    return null;
}
